import { useEffect, useRef, useState } from 'react';
import { fetchPlayersToday } from '../lib/api';

/** Matches the Edge Function's own cache: asking faster than this cannot
 *  return anything newer, it just costs a round trip. */
const EVERY_MS = 5 * 60 * 1000;
/** Tab-flicking should not become a request per flick. */
const FLOOR_MS = 30 * 1000;

/**
 * How many people are playing the thing being translated.
 *
 * Sits at the foot of the rail because it is not navigation and not a queue —
 * it is the reason any of the rest is worth doing. Seventeen thousand unread
 * cards is a chore; seventeen thousand unread cards in front of the people who
 * opened the app this morning is a job.
 *
 * A hidden tab asks for nothing: the browser throttles background timers but
 * does not stop them, and polling on behalf of a window nobody is looking at
 * is pure waste. Coming back to the tab refetches straight away instead, which
 * is both cheaper and fresher than waiting out the interval.
 *
 * Renders nothing at all when PostHog is not configured or the call fails. A
 * broken number in the corner is worse than no number.
 */
export default function LiveUsers() {
  const [users, setUsers] = useState<number | null>(null);
  const lastAt = useRef(0);

  useEffect(() => {
    let alive = true;

    const load = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastAt.current < FLOOR_MS) return;
      lastAt.current = Date.now();
      fetchPlayersToday()
        .then((n) => { if (alive) setUsers(n); })
        .catch(() => { if (alive) setUsers(null); });
    };

    load();
    const timer = setInterval(load, EVERY_MS);
    document.addEventListener('visibilitychange', load);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', load);
    };
  }, []);

  if (users === null) return null;

  return (
    <div className="live" title="Distinct people with any activity in the app since midnight">
      <span className="live-dot" />
      <span className="live-n">{users.toLocaleString()}</span>
      <span className="live-l">played today</span>
    </div>
  );
}
